<div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 space-y-2 border-b border-zinc-200 p-4 dark:border-zinc-800">
    <h4 class="text-sm font-semibold text-primary">Business stats</h4>
    <p class="text-xs text-secondary">
      Search sessions, profile visits, invites, and hires per business (lifetime totals, UTC). Agency
      shown when the brand belongs to one. Requires GodMode, Account Manager, or Creator Admin.
    </p>
    <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <label class="grid w-full max-w-md grid-cols-1 sm:max-w-sm">
          <span class="sr-only">Search businesses</span>
          <input
            type="search"
            class="input-icon col-start-1 row-start-1 text-primary"
            placeholder="Search by business or agency name or ID"
            [value]="searchQuery()"
            (input)="onSearchInput($event)"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-secondary sm:size-4"
            aria-hidden="true"
          >
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
          </svg>
        </label>
        <div class="flex items-center gap-3">
          <label class="shout-toggle" for="business-stats-list-show-shout-brands">
            <input
              id="business-stats-list-show-shout-brands"
              type="checkbox"
              class="shout-toggle__input"
              [checked]="showShoutAgencyBrands()"
              (change)="onShowShoutAgencyBrandsChange($any($event.target).checked)"
            />
            <span class="shout-toggle__slider" aria-hidden="true"></span>
          </label>
          <p class="m-0 text-sm text-secondary">SHOUT brands</p>
        </div>
      </div>
      <p class="m-0 shrink-0 text-sm font-medium tabular-nums text-secondary" aria-live="polite">
        {{ recordCountLabel }}
      </p>
    </div>
  </div>

  @if (loadingState === 'error' && error) {
    <div
      class="mx-4 mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/40"
    >
      <p class="m-0 text-sm text-red-700 dark:text-red-300">{{ error }}</p>
    </div>
  }

  <div class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-950">
    <div
      class="min-h-0 flex-1 overflow-auto scrollbar-transparent"
      #scrollDiv
      infinite-scroll
      [infiniteScrollDistance]="scrollDistance"
      [infiniteScrollUpDistance]="scrollUpDistance"
      [infiniteScrollThrottle]="throttle"
      [infiniteScrollContainer]="scrollDiv"
      (scrolled)="onScrollDown()"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
          <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-zinc-800/80">
            <tr>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('statDate')"
                  [class.dark:text-indigo-400]="isSortedBy('statDate')"
                  [class.text-gray-700]="!isSortedBy('statDate')"
                  [class.dark:text-zinc-300]="!isSortedBy('statDate')"
                  (click)="toggleSort('statDate')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by last activity"
                >
                  <span>Last activity</span>
                  @if (isSortedBy('statDate')) {
                    @if (sortOrder === 'ASC') {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('business')"
                  [class.dark:text-indigo-400]="isSortedBy('business')"
                  [class.text-gray-700]="!isSortedBy('business')"
                  [class.dark:text-zinc-300]="!isSortedBy('business')"
                  (click)="toggleSort('business')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by business"
                >
                  <span>Business</span>
                  @if (isSortedBy('business')) {
                    @if (sortOrder === 'ASC') {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('agency')"
                  [class.dark:text-indigo-400]="isSortedBy('agency')"
                  [class.text-gray-700]="!isSortedBy('agency')"
                  [class.dark:text-zinc-300]="!isSortedBy('agency')"
                  (click)="toggleSort('agency')"
                  [disabled]="isLoading || loadingMore"
                  aria-label="Sort by agency"
                >
                  <span>Agency</span>
                  @if (isSortedBy('agency')) {
                    @if (sortOrder === 'ASC') {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                    }
                  }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                Business ID
              </th>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                Agency ID
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button type="button" class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400" [class.text-indigo-600]="isSortedBy('searches')" [class.dark:text-indigo-400]="isSortedBy('searches')" [class.text-gray-700]="!isSortedBy('searches')" [class.dark:text-zinc-300]="!isSortedBy('searches')" (click)="toggleSort('searches')" [disabled]="isLoading || loadingMore" aria-label="Sort by searches">
                  <span>Searches</span>
                  @if (isSortedBy('searches')) { @if (sortOrder === 'ASC') { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg> } @else { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg> } }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button type="button" class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400" [class.text-indigo-600]="isSortedBy('profileVisits')" [class.dark:text-indigo-400]="isSortedBy('profileVisits')" [class.text-gray-700]="!isSortedBy('profileVisits')" [class.dark:text-zinc-300]="!isSortedBy('profileVisits')" (click)="toggleSort('profileVisits')" [disabled]="isLoading || loadingMore" aria-label="Sort by profile visits">
                  <span>Profile visits</span>
                  @if (isSortedBy('profileVisits')) { @if (sortOrder === 'ASC') { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg> } @else { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg> } }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button type="button" class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400" [class.text-indigo-600]="isSortedBy('invitesSent')" [class.dark:text-indigo-400]="isSortedBy('invitesSent')" [class.text-gray-700]="!isSortedBy('invitesSent')" [class.dark:text-zinc-300]="!isSortedBy('invitesSent')" (click)="toggleSort('invitesSent')" [disabled]="isLoading || loadingMore" aria-label="Sort by invites">
                  <span>Invites</span>
                  @if (isSortedBy('invitesSent')) { @if (sortOrder === 'ASC') { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg> } @else { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg> } }
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button type="button" class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400" [class.text-indigo-600]="isSortedBy('hires')" [class.dark:text-indigo-400]="isSortedBy('hires')" [class.text-gray-700]="!isSortedBy('hires')" [class.dark:text-zinc-300]="!isSortedBy('hires')" (click)="toggleSort('hires')" [disabled]="isLoading || loadingMore" aria-label="Sort by hires">
                  <span>Hires</span>
                  @if (isSortedBy('hires')) { @if (sortOrder === 'ASC') { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg> } @else { <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-3 w-3" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg> } }
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-950">
            @if (isLoading && !hasRowData) {
              <tr>
                <td colspan="9" class="px-4 py-8">
                  <atom-loading-inline title="Loading business stats" message="Fetching rows…" />
                </td>
              </tr>
            } @else if (hasRowData) {
              @for (row of rows; track row.businessAccountId) {
                <tr class="hover:bg-gray-50 dark:hover:bg-zinc-900/50">
                  <td class="px-4 py-3 text-gray-900 dark:text-zinc-100">{{ row.statDate }}</td>
                  <td class="px-4 py-3">
                    <a
                      class="block truncate font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
                      [routerLink]="['/admin-options/business-profile', row.businessAccountId]">
                      {{ row.companyName || row.businessAccountId }}
                    </a>
                  </td>
                  <td class="px-4 py-3 text-secondary">{{ row.agencyName || '—' }}</td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-1.5">
                      <p
                        class="m-0 min-w-0 max-w-[10rem] truncate font-mono text-xs text-gray-900 dark:text-zinc-100"
                        [title]="row.businessAccountId"
                      >
                        {{ row.businessAccountId }}
                      </p>
                      <button
                        type="button"
                        class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                        [attr.aria-label]="isCopied(getCopyKey(row, 'businessAccountId')) ? 'Business ID copied' : 'Copy business ID'"
                        (click)="copyId($event, row.businessAccountId, 'Business ID', getCopyKey(row, 'businessAccountId'))"
                      >
                        @if (isCopied(getCopyKey(row, 'businessAccountId'))) {
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-check-icon lucide-copy-check" aria-hidden="true"><path d="m12 15 2 2 4-4"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                        } @else {
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-icon lucide-copy" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                        }
                      </button>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-1.5">
                      <p
                        class="m-0 min-w-0 max-w-[10rem] truncate font-mono text-xs text-gray-900 dark:text-zinc-100"
                        [title]="row.agencyId || ''"
                      >
                        {{ row.agencyId || '—' }}
                      </p>
                      @if (row.agencyId) {
                        <button
                          type="button"
                          class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
                          [attr.aria-label]="isCopied(getCopyKey(row, 'agencyId')) ? 'Agency ID copied' : 'Copy agency ID'"
                          (click)="copyId($event, row.agencyId, 'Agency ID', getCopyKey(row, 'agencyId'))"
                        >
                          @if (isCopied(getCopyKey(row, 'agencyId'))) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-check-icon lucide-copy-check" aria-hidden="true"><path d="m12 15 2 2 4-4"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                          } @else {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 lucide lucide-copy-icon lucide-copy" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                          }
                        </button>
                      }
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">{{ row.searches }}</td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">{{ row.profileVisits }}</td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">{{ row.invitesSent }}</td>
                  <td class="px-4 py-3 text-right text-gray-900 dark:text-zinc-100">{{ row.hires }}</td>
                </tr>
              }
              @if (loadingMore) {
                <tr>
                  <td colspan="9" class="px-4 py-4 text-center text-xs text-secondary">Loading more…</td>
                </tr>
              }
            } @else if (!isLoading) {
              <tr>
                <td colspan="9" class="px-4 py-8 text-center text-sm text-secondary">No business stats found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
